import { useCallback, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import type { RootState } from '@/store/store'
import { selectActiveMatterId } from '@/matter/store/selectors'
import { selectRecentConversationsByMatter } from '@/chat-common/conversation_list/selectors'
import { selectMatterConversationsError, selectMatterConversationsLoading } from '@/agent/conversations/store/selectors'
import { AgentConversationsActions, type AgentConversation } from '@/agent/conversations/store/slice'
import { useMatterDrafts } from '@/matter/hooks/useMatterDrafts'

const INITIAL_DISPLAY_COUNT = 12
const LOAD_MORE_INCREMENT = 12

export interface UseRecentMatterActivityResult {
  /** The ID of the active matter. */
  matterId: string | null
  /** The list of activity items for the active matter. Currently this only returns conversations. */
  activity: AgentConversation[]
  /** Whether there is more recent activity to load. */
  hasMore: boolean
  /** A function to load more recent activity. */
  handleLoadMore: () => void
  /** True while recent activity can't be shown yet: loading or not-yet-loaded, with nothing cached to fall back on. */
  isLoadingWithoutCachedActivity: boolean
  /** Whether the last recent activity fetch failed. */
  isError: boolean
  /** Force a refetch of the recent activity (e.g. retry after an error). */
  refetch: () => void
}

/**
 * Hook for fetching and managing recent activity for the active matter.
 * Handles conversation list refresh, pagination, and display state.
 *
 * Eventually the API will handle the pagination, and this hook will be removed.
 */
export function useRecentMatterActivity(): UseRecentMatterActivityResult {
  const dispatch = useAppDispatch()

  const matterId = useAppSelector((state: RootState) => selectActiveMatterId(state))
  const recentConversations = useAppSelector((state: RootState) => selectRecentConversationsByMatter(state, { matterId }))
  const isConversationsLoading = useAppSelector((state: RootState) => selectMatterConversationsLoading(state, { matterId }))
  const isError = useAppSelector((state: RootState) => selectMatterConversationsError(state, { matterId }))

  // A matter's drafts refetch on every switch. Hold the recent-activity list in its loading state until the
  // drafts are ready too, so the cards appear with their draft chips instead of the chips popping in a moment later.
  const { isLoadingWithoutCachedDrafts: draftsNotReady } = useMatterDrafts()

  // A background refresh of the conversation list keeps the cached activity on screen instead of flashing
  // skeletons, so only show the loading state when there's nothing cached to fall back on.
  const hasCachedActivity = recentConversations.length > 0
  const isLoadingWithoutCachedActivity = (isConversationsLoading && !hasCachedActivity) || draftsNotReady

  const [displayCount, setDisplayCount] = useState(INITIAL_DISPLAY_COUNT)

  // Reset display count when matter changes
  useEffect(() => {
    setDisplayCount(INITIAL_DISPLAY_COUNT)
  }, [matterId])

  useEffect(() => {
    dispatch(AgentConversationsActions.refreshMatterConversationList({ matterId, forceRefresh: false }))
  }, [matterId, dispatch])

  const visibleConversations = recentConversations.slice(0, displayCount)
  const hasMore = recentConversations.length > displayCount
  const handleLoadMore = useCallback(() => {
    setDisplayCount((prev) => prev + LOAD_MORE_INCREMENT)
  }, [])

  const refetch = useCallback(() => {
    dispatch(AgentConversationsActions.refreshMatterConversationList({ matterId, forceRefresh: true }))
  }, [dispatch, matterId])

  return {
    activity: visibleConversations,
    handleLoadMore,
    hasMore,
    isLoadingWithoutCachedActivity,
    isError,
    refetch,
    matterId,
  }
}
